<template>
  <div class="min-h-screen bg-gray-50">
    <div class="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-4xl font-bold text-gray-900 mb-2">Terms and Conditions</h1>
        <p class="text-gray-600">Last Updated: October 23, 2025</p>
      </div>

      <!-- Content -->
      <div class="bg-white shadow-sm rounded-lg p-8 space-y-8">
        <!-- Introduction -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">1. Agreement to Terms</h2>
          <p class="text-gray-700 leading-relaxed">
            These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") and Peachy Pro Studios ("Company," "we," "us," or "our") governing your access to and use of our web application and mobile application (collectively, the "Services") for video editing, processing, and content creation.
          </p>
          <p class="text-gray-700 leading-relaxed mt-4">
            By accessing or using our Services, you agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you must not access or use our Services.
          </p>
        </section>

        <!-- Eligibility -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">2. Eligibility</h2>
          <p class="text-gray-700 leading-relaxed">
            You must be at least 13 years of age (or the applicable age of consent in your jurisdiction) to use our Services. If you are under 18 years of age, you must have permission from a parent or legal guardian to use the Services. By using our Services, you represent and warrant that you meet these eligibility requirements.
          </p>
          <p class="text-gray-700 leading-relaxed mt-4">
            If you are using the Services on behalf of an organization, you represent that you have the authority to bind that organization to these Terms.
          </p>
        </section>

        <!-- Account Registration -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">3. Account Registration and Security</h2>
          
          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-4">3.1 Account Creation</h3>
          <p class="text-gray-700 leading-relaxed">
            To access certain features of the Services, you must create an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">3.2 Account Security</h3>
          <p class="text-gray-700 leading-relaxed">
            You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized access or use of your account. We are not liable for any losses arising from unauthorized use of your account.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">3.3 Account Suspension or Termination</h3>
          <p class="text-gray-700 leading-relaxed">
            We reserve the right to suspend or terminate your account at any time, with or without notice, if we believe you have violated these Terms or engaged in fraudulent, illegal, or harmful activities.
          </p>
        </section>

        <!-- Services Description -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">4. Description of Services</h2>
          <p class="text-gray-700 leading-relaxed mb-2">
            Peachy Pro Studios provides video editing, processing, and content creation services, including but not limited to:
          </p>
          <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>Upload, storage, and management of photos, videos, and multimedia content</li>
            <li>Video editing, trimming, and assembly tools</li>
            <li>Visual effects, filters, and enhancements</li>
            <li>Audio editing and synchronization</li>
            <li>Collaboration and approval workflows</li>
            <li>Content rendering and export</li>
            <li>Project management and organization features</li>
          </ul>
          <p class="text-gray-700 leading-relaxed mt-4">
            We reserve the right to modify, suspend, or discontinue any aspect of the Services at any time, with or without notice.
          </p>
        </section>

        <!-- User Content -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">5. User Content and Intellectual Property</h2>
          
          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-4">5.1 Your Content</h3>
          <p class="text-gray-700 leading-relaxed">
            You retain all ownership rights to the photos, videos, and other content you upload to our Services ("User Content"). By uploading User Content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, process, adapt, modify, publish, transmit, display, and distribute your User Content solely for the purpose of providing and improving our Services.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">5.2 Content Restrictions</h3>
          <p class="text-gray-700 leading-relaxed mb-2">You agree not to upload, post, or transmit any User Content that:</p>
          <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>Infringes any intellectual property rights, privacy rights, or other rights of any third party</li>
            <li>Contains viruses, malware, or other harmful code</li>
            <li>Is unlawful, defamatory, obscene, pornographic, harassing, or offensive</li>
            <li>Promotes violence, discrimination, or illegal activities</li>
            <li>Contains personal information of minors without appropriate consent</li>
            <li>Violates any applicable laws or regulations</li>
          </ul>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">5.3 Content Monitoring and Removal</h3>
          <p class="text-gray-700 leading-relaxed">
            We reserve the right, but have no obligation, to monitor, review, or remove User Content that violates these Terms or is otherwise objectionable. We may also remove User Content to comply with legal requirements or to protect our rights and interests.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">5.4 Intellectual Property Rights</h3>
          <p class="text-gray-700 leading-relaxed">
            All intellectual property rights in the Services, including software, design, trademarks, and content provided by us, are owned by or licensed to Peachy Pro Studios. You may not copy, modify, distribute, or create derivative works based on our intellectual property without our prior written consent.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">5.5 Representations and Warranties</h3>
          <p class="text-gray-700 leading-relaxed">
            You represent and warrant that you own or have obtained all necessary rights, licenses, consents, and permissions to use and authorize us to use your User Content, including any individuals appearing in photos or videos, and that your User Content does not violate any third-party rights.
          </p>
        </section>

        <!-- Acceptable Use -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">6. Acceptable Use Policy</h2>
          <p class="text-gray-700 leading-relaxed mb-2">You agree not to:</p>
          <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>Use the Services for any illegal purpose or in violation of any laws</li>
            <li>Interfere with or disrupt the Services or servers or networks connected to the Services</li>
            <li>Attempt to gain unauthorized access to any portion of the Services or other systems or networks</li>
            <li>Use any automated means (bots, scrapers, etc.) to access the Services without our permission</li>
            <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity</li>
            <li>Collect or harvest any information from the Services for unauthorized purposes</li>
            <li>Use the Services to transmit spam, chain letters, or other unsolicited communications</li>
            <li>Reverse engineer, decompile, or disassemble any portion of the Services</li>
            <li>Remove or alter any copyright, trademark, or proprietary notices</li>
          </ul>
        </section>

        <!-- Payment and Fees -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">7. Payment and Fees</h2>
          
          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-4">7.1 Subscription Plans</h3>
          <p class="text-gray-700 leading-relaxed">
            Certain features of the Services may require payment of fees. You agree to pay all applicable fees associated with your chosen subscription plan or service package. All fees are non-refundable unless otherwise stated or required by law.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">7.2 Billing</h3>
          <p class="text-gray-700 leading-relaxed">
            By providing payment information, you authorize us to charge your payment method for all fees incurred. Subscription fees will be billed on a recurring basis according to your selected billing cycle. You are responsible for providing accurate payment information and updating it as needed.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">7.3 Price Changes</h3>
          <p class="text-gray-700 leading-relaxed">
            We reserve the right to modify our pricing and fees at any time. We will provide reasonable notice of any price changes. Continued use of the Services after a price change constitutes acceptance of the new pricing.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">7.4 Cancellation and Refunds</h3>
          <p class="text-gray-700 leading-relaxed">
            You may cancel your subscription at any time through your account settings. Cancellation will take effect at the end of your current billing period. We do not provide refunds for partial subscription periods unless required by law.
          </p>
        </section>

        <!-- Data Processing and Storage -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">8. Data Processing and Storage</h2>
          
          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-4">8.1 Processing Activities</h3>
          <p class="text-gray-700 leading-relaxed">
            By uploading User Content to our Services, you authorize us to process, store, and transmit your content for the purpose of providing our video editing and processing services. This includes transcoding, rendering, creating preview versions, and applying edits and effects as requested.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">8.2 Storage Limits</h3>
          <p class="text-gray-700 leading-relaxed">
            Storage capacity may vary depending on your subscription plan. We reserve the right to impose storage limits and to delete content that exceeds those limits or has been inactive for an extended period.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">8.3 Data Backup</h3>
          <p class="text-gray-700 leading-relaxed">
            While we implement backup procedures, you are solely responsible for maintaining your own backups of your User Content. We are not liable for any loss or corruption of User Content.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">8.4 Content Deletion</h3>
          <p class="text-gray-700 leading-relaxed">
            Upon termination or deletion of your account, we will delete your User Content in accordance with our data retention policies. However, some content may remain in backup systems for a limited period.
          </p>
        </section>

        <!-- Third-Party Services -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">9. Third-Party Services and Links</h2>
          <p class="text-gray-700 leading-relaxed">
            Our Services may integrate with or contain links to third-party services, websites, or applications. We are not responsible for the content, privacy practices, or terms of use of any third-party services. Your use of third-party services is at your own risk and subject to their respective terms and conditions.
          </p>
        </section>

        <!-- Disclaimer of Warranties -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">10. Disclaimer of Warranties</h2>
          <p class="text-gray-700 leading-relaxed mb-4">
            THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
          </p>
          <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT</li>
            <li>WARRANTIES THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE</li>
            <li>WARRANTIES REGARDING THE ACCURACY, RELIABILITY, OR QUALITY OF ANY CONTENT OR INFORMATION</li>
          </ul>
          <p class="text-gray-700 leading-relaxed mt-4">
            WE DO NOT WARRANT THAT THE SERVICES WILL MEET YOUR REQUIREMENTS OR THAT ANY DEFECTS WILL BE CORRECTED. YOUR USE OF THE SERVICES IS AT YOUR SOLE RISK.
          </p>
        </section>

        <!-- Limitation of Liability -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">11. Limitation of Liability</h2>
          <p class="text-gray-700 leading-relaxed">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, PEACHY PRO STUDIOS AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING FROM OR RELATING TO:
          </p>
          <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-2">
            <li>Your access to or use of (or inability to access or use) the Services</li>
            <li>Any conduct or content of any third party on the Services</li>
            <li>Any content obtained from the Services</li>
            <li>Unauthorized access, use, or alteration of your content or data</li>
            <li>Loss or corruption of User Content</li>
          </ul>
          <p class="text-gray-700 leading-relaxed mt-4">
            IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS GREATER.
          </p>
          <p class="text-gray-700 leading-relaxed mt-4">
            SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN WARRANTIES OR LIMITATION OF LIABILITY, SO THESE LIMITATIONS MAY NOT APPLY TO YOU.
          </p>
        </section>

        <!-- Indemnification -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">12. Indemnification</h2>
          <p class="text-gray-700 leading-relaxed">
            You agree to indemnify, defend, and hold harmless Peachy Pro Studios and its officers, directors, employees, agents, and affiliates from and against any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising from or relating to:
          </p>
          <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-2">
            <li>Your use of the Services</li>
            <li>Your User Content</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any rights of any third party</li>
            <li>Your violation of any applicable laws or regulations</li>
          </ul>
        </section>

        <!-- Dispute Resolution -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">13. Dispute Resolution and Arbitration</h2>
          
          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-4">13.1 Informal Resolution</h3>
          <p class="text-gray-700 leading-relaxed">
            In the event of any dispute, claim, or controversy arising from or relating to these Terms or the Services, you agree to first contact us to attempt to resolve the dispute informally.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">13.2 Binding Arbitration</h3>
          <p class="text-gray-700 leading-relaxed">
            If we are unable to resolve a dispute informally, you agree that any dispute shall be resolved through binding arbitration in accordance with the rules of a recognized arbitration association, rather than in court, except that you may assert claims in small claims court if your claims qualify.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">13.3 Class Action Waiver</h3>
          <p class="text-gray-700 leading-relaxed">
            YOU AGREE THAT ANY ARBITRATION OR PROCEEDING SHALL BE LIMITED TO THE DISPUTE BETWEEN YOU AND US INDIVIDUALLY. TO THE FULLEST EXTENT PERMITTED BY LAW, YOU WAIVE ANY RIGHT TO PURSUE DISPUTES ON A CLASS-WIDE BASIS OR IN A REPRESENTATIVE CAPACITY.
          </p>
        </section>

        <!-- Governing Law -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">14. Governing Law and Jurisdiction</h2>
          <p class="text-gray-700 leading-relaxed">
            These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Peachy Pro Studios is headquartered, without regard to its conflict of law provisions. Any legal action or proceeding relating to your access to or use of the Services shall be instituted in the courts of that jurisdiction.
          </p>
        </section>

        <!-- Changes to Terms -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">15. Changes to Terms</h2>
          <p class="text-gray-700 leading-relaxed">
            We reserve the right to modify these Terms at any time. We will provide notice of material changes by posting the updated Terms on our website and updating the "Last Updated" date. Your continued use of the Services after such changes constitutes your acceptance of the modified Terms. If you do not agree to the modified Terms, you must discontinue use of the Services.
          </p>
        </section>

        <!-- Termination -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">16. Termination</h2>
          <p class="text-gray-700 leading-relaxed">
            We may terminate or suspend your access to the Services immediately, without prior notice or liability, for any reason, including if you breach these Terms. Upon termination, your right to use the Services will cease immediately. All provisions of these Terms which by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, and limitations of liability.
          </p>
          <p class="text-gray-700 leading-relaxed mt-4">
            You may terminate your account at any time by contacting us or through your account settings.
          </p>
        </section>

        <!-- Miscellaneous -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">17. Miscellaneous</h2>
          
          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-4">17.1 Entire Agreement</h3>
          <p class="text-gray-700 leading-relaxed">
            These Terms, together with our Privacy Policy, constitute the entire agreement between you and Peachy Pro Studios regarding the Services and supersede all prior agreements and understandings.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">17.2 Severability</h3>
          <p class="text-gray-700 leading-relaxed">
            If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">17.3 Waiver</h3>
          <p class="text-gray-700 leading-relaxed">
            No waiver of any term of these Terms shall be deemed a further or continuing waiver of such term or any other term.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">17.4 Assignment</h3>
          <p class="text-gray-700 leading-relaxed">
            You may not assign or transfer these Terms or your rights and obligations hereunder without our prior written consent. We may assign these Terms without restriction.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">17.5 Force Majeure</h3>
          <p class="text-gray-700 leading-relaxed">
            We shall not be liable for any failure or delay in performance due to causes beyond our reasonable control, including acts of God, natural disasters, war, terrorism, pandemics, or government actions.
          </p>
        </section>

        <!-- Contact Information -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">18. Contact Information</h2>
          <p class="text-gray-700 leading-relaxed mb-4">
            If you have any questions or concerns about these Terms, please contact us:
          </p>
          <div class="bg-gray-50 p-6 rounded-lg">
            <p class="text-gray-700"><strong>Peachy Pro Studios</strong></p>
            <p class="text-gray-700 mt-2">Email: <a href="mailto:legal@peachyprostudios.com" class="text-blue-600 hover:text-blue-800">legal@peachyprostudios.com</a></p>
            <p class="text-gray-700 mt-1">Support: <a href="mailto:support@peachyprostudios.com" class="text-blue-600 hover:text-blue-800">support@peachyprostudios.com</a></p>
          </div>
        </section>

        <!-- Acknowledgment -->
        <section class="border-t border-gray-200 pt-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">19. Acknowledgment</h2>
          <p class="text-gray-700 leading-relaxed">
            BY USING THE SERVICES, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS AND CONDITIONS. IF YOU DO NOT AGREE TO THESE TERMS, YOU MUST NOT ACCESS OR USE OUR SERVICES.
          </p>
        </section>
      </div>

      <!-- Back to Home -->
      <div class="mt-8 text-center">
        <NuxtLink to="/" class="text-blue-600 hover:text-blue-800 font-medium">
          ← Back to Home
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
// Set page metadata
useHead({
  title: 'Terms and Conditions - Peachy Pro Studios',
  meta: [
    {
      name: 'description',
      content: 'Terms and Conditions for Peachy Pro Studios web and mobile applications. Review our terms of service for video editing and content processing services.'
    }
  ]
})
</script>

<style scoped>
/* Additional custom styles if needed */
</style>